import { useCollectionServices } from '../../contexts/CollectionSourceContext';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import ShowChart from '@mui/icons-material/ShowChart';
import StudySectionIcon from './StudySectionIcon';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { FitbitMetric, FitbitReadMode, FitbitReadParticipant } from '../../services/fitbitCollectionDataService';
import FitbitCollectionStudyOverview from './FitbitCollectionStudyOverview';
import FitbitStoredDataExplorer from './fitbit/FitbitStoredDataExplorer';
import FitbitVisualizationExplorer from './fitbit/FitbitVisualizationExplorer';
import { validRange } from './fitbit/dataAvailability';
import FitbitResearcherDashboard from './fitbit/FitbitResearcherDashboard';
import { availabilityLabel } from './fitbit/StoredDateRange';
import FitbitExportParticipantPicker from './fitbit/FitbitExportParticipantPicker';
import DateRangeControl from '../common/DateRangeControl';
import FitbitExtractionHistory from './fitbit/FitbitExtractionHistory';
import OutdatedStudyNotice from '../common/OutdatedStudyNotice';
import { useParticipantAvailability } from './fitbit/useParticipantAvailability';

const labels = { visualization: 'Fitbit visualizations', coverage: 'Fitbit data coverage',
  adherence: 'Fitbit adherence', export: 'Export Fitbit data', alerts: 'Fitbit adherence alerts' };
const today = () => new Date().toISOString().slice(0, 10);
const before = () => new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);

export default function FitbitCollectionDataView(props: { mode: FitbitReadMode; studyId?: string; legacy?: React.ReactNode; embedded?: boolean }) {
  const { source } = useCollectionServices();
  if (source.id === 'healthkit') return <>{props.legacy}</>;
  if (props.mode === 'coverage' || props.mode === 'adherence' || props.mode === 'alerts') return <FitbitResearcherDashboard {...props} mode={props.mode} />;
  return <FitbitCollectionMeasurementsView {...props} />;
}

function FitbitCollectionMeasurementsView({ mode, studyId: suppliedStudy, legacy, embedded = false }: {
  mode: FitbitReadMode; studyId?: string; legacy?: React.ReactNode; embedded?: boolean;
}) {
  const { loadFitbitParticipants, readFitbitAvailability, source } = useCollectionServices();
  const params = useParams();
  const [search] = useSearchParams();
  const studyId = suppliedStudy || params.studyId || search.get('study') || '';
  const [roster, setRoster] = useState<{ studyId: string; sourceId: string; revision: number; participants: FitbitReadParticipant[] } | null>(null);
  const [catalog, setCatalog] = useState<FitbitMetric[]>([]);
  const [hasLegacy, setHasLegacy] = useState(false);
  const [hasOutdatedFitbit, setHasOutdatedFitbit] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [overview, setOverview] = useState(false);
  const [participantId, setParticipantId] = useState('');
  const [exportIds, setExportIds] = useState<string[]>([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [startDate, setStart] = useState(before);
  const [endDate, setEnd] = useState(today);
  const [error, setError] = useState('');
  const [revision, refresh] = useState(0);
  const [extractionRevision, refreshExtractions] = useState(0);
  const rosterParticipants = roster?.studyId === studyId && roster.sourceId === source.id && roster.revision === revision ? roster.participants : null;
  const { participants, readAvailability } = useParticipantAvailability(studyId, revision, rosterParticipants, readFitbitAvailability, mode === 'export');
  const selection = participants?.find(p => p.participantId === participantId);
  const inputsValid = validRange({ startDate, endDate });

  useEffect(() => { setExportIds([]); setParticipantId(''); setOverview(false); setExportBusy(false); }, [studyId]);
  useEffect(() => {
    let current = true;
    setRoster(null); setCatalog([]); setError('');
    if (!studyId) { setError('Open this page from a study.'); return; }
    loadFitbitParticipants(studyId).then(result => {
      if (!current) return;
      setRoster({ studyId, sourceId: source.id, revision, participants: result.participants }); setHasLegacy(result.hasLegacyParticipants);
      setHasOutdatedFitbit(Boolean(result.hasOutdatedFitbitParticipants));
      setCatalog(result.catalog || []);
      setExportIds(current => current.filter(id => result.participants.some(item => item.participantId === id && item.enrollmentStatus === 'completed')));
      setParticipantId(current => result.participants.some(item => item.participantId === current) ? current : '');
    }).catch(() => { if (current) setError(`Could not load ${source.label} participants. Please retry.`); });
    return () => { current = false; };
  }, [studyId, revision, loadFitbitParticipants, source.id, source.label]);

  if (mode === 'export' && (showLegacy || (participants?.length === 0 && hasOutdatedFitbit))) return <>
    {Boolean(participants?.length) && <Button onClick={() => setShowLegacy(false)}>Back to Fitbit data</Button>}
    <OutdatedStudyNotice expanded enrollmentsOnly={Boolean(participants?.length)} />
  </>;
  if (mode !== 'export' && showLegacy && legacy) return <>
    <Button onClick={() => setShowLegacy(false)}>Back to Fitbit data</Button>{legacy}</>;
  const participantOptions = participants?.map(p => <MenuItem key={p.participantId} value={p.participantId} sx={{ whiteSpace: 'normal' }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2">{p.name} · {p.participantId}{p.enrollmentStatus === 'pending' ? ' · Pending' : ''}</Typography>
      {mode === 'export' && p.enrollmentStatus === 'completed' && <Typography variant="caption" color="text.secondary">{availabilityLabel(p.dataAvailability)}</Typography>}
    </Box>
  </MenuItem>);
  return <Container maxWidth={embedded || mode === 'export' ? false : 'xl'} disableGutters={embedded || mode === 'export'} sx={{ py: embedded || mode === 'export' ? 0 : 3 }}>
    {!embedded && !suppliedStudy && <Button component={RouterLink} variant="outlined" startIcon={<ArrowBack />}
      to={studyId ? `/researcher/study/${encodeURIComponent(studyId)}` : '/researcher/dashboard'} sx={{ mb: 2 }}>
      {studyId ? 'Back to study' : 'Back to studies'}
    </Button>}
    {mode !== 'export' && <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <StudySectionIcon><ShowChart /></StudySectionIcon>
        <Typography variant={embedded ? 'h5' : 'h4'} component={embedded ? 'h2' : 'h1'}>{labels[mode].replace('Fitbit', source.label)}</Typography>
      </Stack>
      <Button startIcon={<Refresh />} onClick={() => refresh(n => n + 1)}>Refresh</Button>
      {participants && participants.length > 0 && <Button disabled={!selection} onClick={() => setOverview(value => !value)}>{overview ? 'Hide study overview' : 'Study overview'}</Button>}
      {hasLegacy && legacy && <Button onClick={() => setShowLegacy(true)}>Older Fitbit and other data sources</Button>}
    </Stack>}
    {mode === 'export' && <>
      <FitbitExportParticipantPicker participants={participants || []} selectedIds={exportIds} onChange={setExportIds} disabled={exportBusy}
        onRefresh={() => refresh(value => value + 1)} />
      {hasOutdatedFitbit && <Button size="small" onClick={() => setShowLegacy(true)} sx={{ mb: 1.5 }}>Older Fitbit enrollments · Outdated</Button>}
    </>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {participants === null && !error && <CircularProgress aria-label="Loading participants" />}
    {mode !== 'export' && participants && participants.length > 0 && <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
      <TextField select label="Participant" value={participantId} onChange={e => { setParticipantId(e.target.value); if (!e.target.value) setOverview(false); }} sx={{ minWidth: 250 }}>
        <MenuItem value="">Choose a participant</MenuItem>
        {participantOptions}
      </TextField>
      <DateRangeControl key={studyId + ':' + participantId} value={{ startDate, endDate }} available={selection?.dataAvailability?.range}
        disabled={!selection} onChange={range => { setStart(range.startDate); setEnd(range.endDate); }} />
    </Stack>}
    {mode !== 'export' && !inputsValid && <Alert severity="warning">Choose a valid date range of up to 10 years.</Alert>}
    {mode !== 'export' && participants && participants.length > 0 && !selection && <Alert severity="info">Choose a participant to view their data.</Alert>}
    {overview && selection && participants && inputsValid && <FitbitCollectionStudyOverview studyId={studyId} participants={participants}
      startDate={startDate} endDate={endDate} onSelect={setParticipantId} />}
    {selection?.enrollmentStatus === 'pending' && <Alert severity="info">This participant has not completed enrollment.</Alert>}
    {participants?.length === 0 && <Alert severity="info">No Fitbit participants are available in this study.</Alert>}
    {mode === 'export' && participants && participants.length > 0 && exportIds.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>Choose one or more enrolled participants to configure the extraction.</Alert>}
    {mode === 'export' && participants && exportIds.length > 0 && <FitbitStoredDataExplorer key={studyId + ':' + [...exportIds].sort().join(',')}
      studyId={studyId} participantId={exportIds[0]} participants={participants.filter(item => exportIds.includes(item.participantId))}
      availabilityReader={readAvailability}
      revision={revision} onBusyChange={setExportBusy} onExtractionCreated={() => refreshExtractions(value => value + 1)} onParticipantAvailability={(id, availability) => {
        setRoster(previous => previous && ({ ...previous, participants: previous.participants.map(participant => participant.participantId === id
          ? { ...participant, dataAvailability: { range: availability.range, indexedAt: availability.indexedAt } } : participant) }));
      }} />}
    {mode === 'export' && studyId && <FitbitExtractionHistory studyId={studyId} revision={revision + extractionRevision} />}
    {mode === 'visualization' && selection?.enrollmentStatus === 'completed' && <FitbitVisualizationExplorer key={`${studyId}:${participantId}`}
      studyId={studyId} participantId={participantId} range={{ startDate, endDate }} revision={revision}
      catalog={catalog} onUseDates={range => { setStart(range.startDate); setEnd(range.endDate); }} />}
  </Container>;
}
